import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Database, MapPinned, Trash2, RefreshCw } from 'lucide-react';
import type { PoiStats } from '@ooh/contracts';
import { getCategoryLabel } from '../utils/category-label';
import { Button } from '@ooh/ui/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import { Separator } from '@ooh/ui/components/separator';
import type { WithCallWithAuth } from '../api/queries';
import { PoiList } from './poi-list';
import { PoiImportPanel } from './poi-import-panel';

// ─── Stat card ────────────────────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
  loading,
}: {
  icon: React.ElementType;
  label: string;
  value: number | null;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-4">
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted">
        <Icon className="h-5 w-5 text-muted-foreground" strokeWidth={1.6} />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-[12px] text-muted-foreground leading-tight">{label}</span>
        {loading ? (
          <div className="h-6 w-12 mt-0.5 rounded bg-muted animate-pulse" />
        ) : (
          <span className="text-[22px] font-semibold leading-tight tabular-nums text-foreground">
            {value?.toLocaleString() ?? '—'}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────

interface PoiAdminDashboardProps {
  stats: PoiStats | undefined;
  statsLoading: boolean;
  lastImportLabel: string | null;
  auth: WithCallWithAuth;
  onDelete: (poi: import('@ooh/contracts').Poi) => void;
  onCreate: () => void;
  onRefresh: () => void;
  /**
   * Manual-import panels for other admin data sources (transport layers,
   * administrative boundaries). Rendered by the route, which composes
   * multiple features — see WEB_ARCHITECTURE.md's feature-boundary rule.
   */
  transportImportPanel: React.ReactNode;
  adminBoundaryImportPanel: React.ReactNode;
}

// eslint-disable-next-line complexity -- dashboard renders multiple conditional sections
export function PoiAdminDashboard({
  stats,
  statsLoading,
  lastImportLabel,
  auth,
  onDelete,
  onCreate,
  onRefresh,
  transportImportPanel,
  adminBoundaryImportPanel,
}: PoiAdminDashboardProps) {
  const { t, i18n } = useTranslation('map');
  const [showCustomList, setShowCustomList] = React.useState(false);

  return (
    <div className="p-6 max-w-5xl space-y-6" data-testid="admin-pois-page">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={Database}
          label={t('admin.pois.stats.osmCount')}
          value={stats?.totalOsm ?? null}
          loading={statsLoading}
        />
        <StatCard
          icon={MapPinned}
          label={t('admin.pois.stats.customCount')}
          value={stats?.totalCustom ?? null}
          loading={statsLoading}
        />
        <StatCard
          icon={Trash2}
          label={t('admin.pois.stats.deletedCount')}
          value={stats?.totalDeleted ?? null}
          loading={statsLoading}
        />
      </div>

      {/* Last import row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} />
          <span>
            {t('admin.pois.stats.lastImport')}:{' '}
            {statsLoading ? (
              <span className="inline-block h-4 w-28 align-middle rounded bg-muted animate-pulse" />
            ) : (
              <span className="text-foreground font-medium">{lastImportLabel}</span>
            )}
          </span>
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={onRefresh}
          aria-label={t('admin.pois.stats.lastImport')}
        >
          <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.8} />
        </Button>
      </div>

      <Separator />

      {/* Category breakdown */}
      <div>
        <h2 className="text-[14px] font-semibold text-foreground mb-3">
          {t('admin.pois.stats.categoryBreakdown')}
        </h2>
        {statsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key -- shimmer rows
              <div key={i} className="h-6 w-full rounded bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <Table className="text-[13px]">
              <TableHeader>
                <TableRow>
                  <TableHead caps={false} className="px-4 py-2.5">
                    {t('admin.pois.columns.category')}
                  </TableHead>
                  <TableHead caps={false} className="w-24 px-4 py-2.5 text-right">
                    {t('admin.pois.stats.osmCount')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(stats?.categoryCounts ?? []).map((cat, idx) => (
                  <TableRow
                    key={`${cat.osm_key}-${cat.osm_value}`}
                    className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                  >
                    <TableCell className="px-4 py-2 text-foreground">
                      {getCategoryLabel(cat, t)}
                    </TableCell>
                    <TableCell className="px-4 py-2 text-right tabular-nums font-medium text-foreground">
                      {cat.count.toLocaleString(i18n.language)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Separator />

      {/* Custom POI management */}
      <div>
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-[14px] font-semibold text-foreground">
              {t('admin.pois.stats.manageCta')}
            </h2>
            <p className="text-[12px] text-muted-foreground max-w-lg">
              {t('admin.pois.stats.manageDesc')}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setShowCustomList((v) => !v)}
              data-testid="toggle-custom-poi-list"
            >
              {showCustomList ? t('admin.pois.hideListLabel') : t('admin.pois.showListLabel')}
            </Button>
            <Button
              size="xs"
              onClick={() => {
                setShowCustomList(true);
                onCreate();
              }}
              data-testid="add-poi-button"
            >
              {t('admin.pois.addButton')}
            </Button>
          </div>
        </div>

        {showCustomList ? (
          <div className="mt-4">
            <PoiList auth={auth} onDelete={onDelete} onCreate={onCreate} />
          </div>
        ) : null}
      </div>

      <Separator />

      {/* Manual import trigger */}
      <PoiImportPanel auth={auth} />

      <Separator />

      {/* Transport layers manual import trigger */}
      {transportImportPanel}

      <Separator />

      {/* Administrative boundaries manual import trigger */}
      {adminBoundaryImportPanel}

      {/* Import note */}
      <p className="text-[11px] text-muted-foreground border-t border-border pt-4">
        {t('admin.pois.stats.importNote')}
      </p>
    </div>
  );
}
